import apiClient from './api.ts';
import Cookies from "js-cookie";
import { TwoFactorMethod } from '@/types/two_factor_schema.ts';
import { AxiosResponse } from 'axios';

export interface LoginRequest {
    username: string;
    password: string;
    remember_me?: boolean;
}

export interface LoginResponse {
    success: boolean;
    requires_2fa: boolean;
    session_id?: string;
    access_token?: string;
    refresh_token?: string;
    user?: any;
    message?: string;
    // Firebase custom token for Firestore authentication
    firebase_token?: string;
    // Authorization data wrapper
    authorization?: {
        access_token: string;
        refresh_token: string;
        expires_in?: number;
    };
    // 2FA related fields
    available_methods?: { method: 'app' | 'sms' | 'email' | 'passkey' }[];
    method?: 'app' | 'sms' | 'email' | 'passkey';
    // Wrapped data for some API responses
    data?: {
        authorization?: {
            access_token: string;
            refresh_token: string;
            expires_in?: number;
        };
        user?: any;
        firebase_token?: string;
    };
}

export interface Verify2FARequest {
    code: string;
    session_id: string;
    remember_device?: boolean;
    method?: TwoFactorMethod
}

export interface VerifyRecoveryRequest {
    recovery_code: string;
    session_id: string;
}

export default {
    async login(username: string, password: string, remember_me: boolean = false): Promise<AxiosResponse<{ data: LoginResponse }>> {
        const formData = new FormData();
        formData.append("username", username);
        formData.append("password", password);
        formData.append("remember_me", remember_me.toString());
        return await apiClient.post(`auth/login`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
        );
    },

    async verify2FA(request: Verify2FARequest): Promise<AxiosResponse<{ data: LoginResponse }>> {
        return await apiClient.post(`auth/verify-2fa`, request);
    },

    async verifyRecovery(request: VerifyRecoveryRequest): Promise<AxiosResponse<{ data: LoginResponse }>> {
        return await apiClient.post(`auth/verify-recovery`, request);
    },

    // Passkey authentication methods
    async passkeyAuthenticateBegin(request?: { username: string, is_forced: boolean }): Promise<AxiosResponse<{ data: any }>> {
        return await apiClient.post(`auth/passkey/authenticate/begin`, request || {});
    },

    async passkeyAuthenticateFinish(credentials: any): Promise<{ data: LoginResponse }> {
        return await apiClient.post(`auth/passkey/authenticate/finish?session_id=${credentials.session_id}`, { ...credentials.credential });
    },

    // User lookup for passkey authentication
    async getUserIdByUsername(username: string): Promise<{ data: { user_id: number } }> {
        return await apiClient.post(`auth/user-lookup`, { username });
    },

    async resend2FACode(session_id: string, method: 'sms' | 'email' = 'sms') {
        return await apiClient.post(`auth/resend-2fa`, { session_id, method });
    },

    logout() {
        localStorage.removeItem('user');
        localStorage.removeItem('school');
        localStorage.removeItem('2fa_session');
        Cookies.remove('ssm-session');
        Cookies.remove('ssm-session-expire');
    },

    setUser(user: any) {
        localStorage.setItem('user', JSON.stringify(user));
    },

    getUser() {
        return JSON.parse(localStorage.getItem('user') || '{}');
    },

    // 2FA Session Management
    set2FASession(sessionData: { session_id: string; expires_at: string; requires_2fa: boolean }) {
        localStorage.setItem('2fa_session', JSON.stringify(sessionData));
    },

    get2FASession() {
        const session = localStorage.getItem('2fa_session');
        if (!session) return null;

        try {
            const sessionData = JSON.parse(session);
            // Check if session has expired
            if (new Date(sessionData.expires_at) < new Date()) {
                this.clear2FASession();
                return null;
            }
            return sessionData;
        } catch {
            this.clear2FASession();
            return null;
        }
    },

    clear2FASession() {
        localStorage.removeItem('2fa_session');
    },

    async refreshToken() {
        const user = this.getUser();
        let schoolId = '';
        try {
            const school = JSON.parse(localStorage.getItem('school') || '{}');
            schoolId = school?.id ? String(school.id) : '';
        } catch {
            schoolId = '';
        }
        const response = await apiClient.post('auth/refresh', {
            refresh_token: user.refresh_token
        }, {
            headers: schoolId ? { 'X-School-ID': schoolId } : undefined
        });
        return response.data;
    },

    async registerSchool() {
        return await apiClient.post(``);
    },

    async getUserProfile() {
        return await apiClient.get('users/profile');
    },

    async getUserRoles(userId: number) {
        return await apiClient.get(`roles/user/${userId}`);
    },

    async updateUserProfile(profileData: {
        first_name?: string;
        last_name?: string;
        email?: string;
        phone?: string;
        bio?: string;
        location?: string;
        avatar?: string;
    }) {
        return await apiClient.put('users/profile', profileData);
    },

    // Password and Security
    async changePassword(currentPassword: string, newPassword: string, confirmPassword: string) {
        return await apiClient.post('auth/change-password', {
            current_password: currentPassword,
            new_password: newPassword,
            confirm_password: confirmPassword
        });
    },

    async forgotPassword(email: string) {
        return await apiClient.post('auth/forgot-password', { email });
    },

    async resetPassword(token: string, password: string, confirmPassword: string) {
        return await apiClient.post('auth/reset-password', {
            token,
            password,
            confirm_password: confirmPassword
        });
    },

    // Session Management
    async getActiveSessions() {
        return await apiClient.get('auth/sessions');
    },

    async revokeSession(sessionId: string) {
        return await apiClient.delete(`auth/sessions/${sessionId}`);
    },

    async revokeAllSessions() {
        return await apiClient.delete('auth/sessions');
    },

    // Multi-branch authentication
    async getSchoolsAndBranches() {
        return await apiClient.get('auth/user/schools-branches');
    },

    async switchBranch(schoolId: string, branchId: string | null) {
        return await apiClient.post('auth/user/switch-branch', {
            school_id: schoolId,
            branch_id: branchId
        });
    }
};
